import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { tasksAPI } from '../services/api';

const TaskReport = () => {
  const today = new Date().toISOString().split('T')[0];
  const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(nextMonth);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [dateError, setDateError] = useState('');

  // Filter state
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const fetchTasks = async () => {
    setDateError('');
    if (!startDate || !endDate) {
      setDateError('Please select both a start and end date.');
      return;
    }
    if (endDate < startDate) {
      setDateError('End date cannot be earlier than start date.');
      return;
    }
    try {
      setLoading(true);
      const res = await tasksAPI.getByDateRange(startDate, endDate);
      setTasks(res.data);
      setSearched(true);
      // Reset filters on new search
      setFilterStatus('All');
      setFilterPriority('All');
      setFilterProject('All');
      setSearchTerm('');
      setShowCompleted(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') fetchTasks();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const getDaysUntilDue = (plannedEnd) => {
    if (!plannedEnd) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(plannedEnd + 'T12:00:00');
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  // Unique project names for filter dropdown
  const projectOptions = ['All', ...Array.from(new Set(tasks.map(t => t.project_name).filter(Boolean)))];

  // Apply filters
  const filteredTasks = tasks.filter(task => {
    const matchesCompleted = showCompleted || task.status_id !== 5;
    const matchesStatus = filterStatus === 'All' || task.status_name === filterStatus || (!task.status_name && filterStatus === 'Not Started');
    const matchesPriority = filterPriority === 'All' || task.priority === filterPriority;
    const matchesProject = filterProject === 'All' || task.project_name === filterProject;
    const matchesSearch = !searchTerm ||
      task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.assigned_to_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.project_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCompleted && matchesStatus && matchesPriority && matchesProject && matchesSearch;
  });

  // Summary counts
  const overdue   = filteredTasks.filter(t => getDaysUntilDue(t.planned_end) < 0).length;
  const dueSoon   = filteredTasks.filter(t => { const d = getDaysUntilDue(t.planned_end); return d !== null && d >= 0 && d <= 7; }).length;
  const completed = filteredTasks.filter(t => t.status_id === 5).length;
  const slipping  = filteredTasks.filter(t => t.is_slipping).length;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">📅 Task Report</h2>
          <p className="page-subtitle">View tasks due within a specific date range</p>
        </div>
      </div>

      {/* Date Range Picker Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Select Date Range</h3>
        </div>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0, flex: '1', minWidth: '160px' }}>
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="form-group" style={{ margin: 0, flex: '1', minWidth: '160px' }}>
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={fetchTasks}
            disabled={loading}
            style={{ minWidth: '140px', height: '46px' }}
          >
            {loading ? '⏳ Loading...' : '🔍 Search Tasks'}
          </button>
        </div>
        {dateError && (
          <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', borderLeft: '4px solid #e74c3c', color: '#991b1b', fontSize: '14px' }}>
            ❌ {dateError}
          </div>
        )}
      </div>

      {/* Results */}
      {searched && (
        <>
          {/* Summary strip */}
          {tasks.length > 0 && (
            <div style={{ display: 'flex', gap: '16px', margin: '0 0 20px 0', flexWrap: 'wrap' }}>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flex: 1, minWidth: '120px', textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: '#2c3e50' }}>{tasks.length}</div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Total Tasks</div>
              </div>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flex: 1, minWidth: '120px', textAlign: 'center', borderLeft: overdue > 0 ? '4px solid #e74c3c' : 'none' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: overdue > 0 ? '#e74c3c' : '#2c3e50' }}>{overdue}</div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Overdue</div>
              </div>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flex: 1, minWidth: '120px', textAlign: 'center', borderLeft: dueSoon > 0 ? '4px solid #f59e0b' : 'none' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: dueSoon > 0 ? '#92400e' : '#2c3e50' }}>{dueSoon}</div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Due in 7 Days</div>
              </div>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flex: 1, minWidth: '120px', textAlign: 'center', borderLeft: slipping > 0 ? '4px solid #f59e0b' : 'none' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: slipping > 0 ? '#d97706' : '#2c3e50' }}>{slipping}</div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Slipping</div>
              </div>
              <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flex: 1, minWidth: '120px', textAlign: 'center', borderLeft: '4px solid #27ae60' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: '#27ae60' }}>{completed}</div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Completed</div>
              </div>
            </div>
          )}

          {/* Filters */}
          {tasks.length > 0 && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="🔍 Search task / person / project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 2, minWidth: '200px', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
              />
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <option value="All">All Statuses</option>
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="On Hold">On Hold</option>
              </select>
              <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} style={{ padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <option value="All">All Priorities</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
              <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', maxWidth: '200px' }}>
                {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {/* Completed toggle */}
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                style={{
                  padding: '10px 16px',
                  border: `2px solid ${showCompleted ? '#27ae60' : '#ddd'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: showCompleted ? '#f0fdf4' : '#fff',
                  color: showCompleted ? '#166534' : '#7f8c8d',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
                title={showCompleted ? 'Click to hide completed tasks' : 'Click to show completed tasks'}
              >
                {showCompleted ? '✅ Completed: Shown' : '☑️ Completed: Hidden'}
              </button>
              {(filterStatus !== 'All' || filterPriority !== 'All' || filterProject !== 'All' || searchTerm || showCompleted) && (
                <button
                  onClick={() => { setFilterStatus('All'); setFilterPriority('All'); setFilterProject('All'); setSearchTerm(''); setShowCompleted(false); }}
                  style={{ padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', background: '#fff', cursor: 'pointer', color: '#e74c3c' }}
                >
                  ✕ Clear filters
                </button>
              )}
            </div>
          )}

          {/* Results table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="card-header" style={{ padding: '16px 24px' }}>
              <h3 className="card-title">
                Tasks Due: {formatDate(startDate)} → {formatDate(endDate)}
              </h3>
              <span style={{ fontSize: '13px', color: '#7f8c8d' }}>
                {filteredTasks.length} of {tasks.length} task{tasks.length !== 1 ? 's' : ''}
              </span>
            </div>

            {filteredTasks.length === 0 ? (
              <div className="empty-state" style={{ padding: '60px 20px' }}>
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-text">
                  {tasks.length === 0 ? 'No tasks due in this period' : 'No tasks match the current filters'}
                </div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Task Name</th>
                      <th>Project</th>
                      <th>Assigned To</th>
                      <th>Planned Dates</th>
                      <th>Due In</th>
                      <th>Progress</th>
                      <th>Status</th>
                      <th>Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map((task) => {
                      const daysUntil = getDaysUntilDue(task.planned_end);
                      const isOverdue = daysUntil !== null && daysUntil < 0;
                      const isDueSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;

                      return (
                        <tr key={task.task_id} style={{ background: task.is_slipping ? '#fffbeb' : isOverdue ? '#fff5f5' : 'inherit' }}>
                          <td>
                            <strong>{task.task_name}</strong>
                            {task.description && (
                              <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>
                                {task.description.substring(0, 50)}{task.description.length > 50 ? '...' : ''}
                              </div>
                            )}
                          </td>
                          <td>
                            <Link to={`/projects/${task.project_id}`} style={{ fontWeight: '500' }}>
                              {task.project_name || '—'}
                            </Link>
                          </td>
                          <td>
                            {task.assigned_to_name ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '28px', height: '28px', borderRadius: '50%',
                                  background: '#3498db', color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '11px', fontWeight: '600', flexShrink: 0
                                }}>
                                  {task.assigned_to_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                                </div>
                                <span style={{ fontSize: '14px' }}>{task.assigned_to_name}</span>
                              </div>
                            ) : (
                              <span style={{ color: '#95a5a6', fontSize: '13px' }}>Unassigned</span>
                            )}
                          </td>
                          <td style={{ fontSize: '13px', color: '#7f8c8d', whiteSpace: 'nowrap' }}>
                            {formatDate(task.planned_start)} → {formatDate(task.planned_end)}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {task.is_slipping ? (
                              <span className="badge badge-warning">⚠ Slipping {Math.abs(daysUntil)}d</span>
                            ) : daysUntil === null ? '—' : isOverdue ? (
                              <span className="badge badge-danger">{Math.abs(daysUntil)} day{Math.abs(daysUntil) !== 1 ? 's' : ''} overdue</span>
                            ) : daysUntil === 0 ? (
                              <span className="badge badge-danger">Due today</span>
                            ) : isDueSoon ? (
                              <span className="badge badge-warning">{daysUntil} day{daysUntil !== 1 ? 's' : ''}</span>
                            ) : (
                              <span className="badge badge-info">{daysUntil} day{daysUntil !== 1 ? 's' : ''}</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="progress" style={{ flex: 1, minWidth: '70px' }}>
                                <div
                                  className={`progress-bar ${
                                    task.progress >= 75 ? 'success' :
                                    task.progress >= 50 ? 'warning' :
                                    task.progress >= 25 ? 'orange' : 'danger'
                                  }`}
                                  style={{ width: `${task.progress || 0}%` }}
                                />
                              </div>
                              <span style={{ fontSize: '13px', minWidth: '35px' }}>{task.progress || 0}%</span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${
                              task.status_id === 5 ? 'badge-success' :
                              task.status_id === 2 ? 'badge-warning' :
                              task.status_id === 3 ? 'badge-warning' :
                              'badge-info'
                            }`} style={{ fontSize: '12px' }}>
                              {task.status_name || 'Not Started'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              task.priority === 'High' || task.priority === 'Critical' ? 'badge-danger' :
                              task.priority === 'Medium' ? 'badge-warning' : 'badge-info'
                            }`} style={{ fontSize: '12px' }}>
                              {task.priority || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Initial prompt */}
      {!searched && !loading && (
        <div className="empty-state" style={{ marginTop: '40px' }}>
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-text">Select a date range and click Search to view tasks</div>
        </div>
      )}
    </div>
  );
};

export default TaskReport;
